import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { allowedUploadExtensions } from '@app/core/constants';
import { UploadFile } from '@app/core/model';
import { NotificationService } from '@app/core/services';
import { UploadError } from '@app/shared/models';
import { PrimeNgModule } from '@app/shared/primeng/prime-ng.module';
import { isFileNameValid } from '@mynexus/mynexus-ui-lib';
import { SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { SharedErrorsMessagesComponent } from '../shared-errors-messages/shared-errors-messages.component';

@Component({
    selector: 'mnc-document-shared-document-upload-emitter',
    templateUrl: './shared-document-upload-emitter.component.html',
    styleUrls: ['./shared-document-upload-emitter.component.scss'],
    imports: [
        CommonModule,
        SharedModule,
        MultiSelectModule,
        ButtonModule,
        ReactiveFormsModule,
        InputTextModule,
        TableModule,
        MessageModule,
        SharedErrorsMessagesComponent,
        TooltipModule,
        PrimeNgModule,
    ]
})
export class SharedDocumentUploadEmitterComponent {
  @ViewChild('inputFile', { static: true })
  inputFile!: ElementRef<HTMLInputElement>;

  // Required Inputs
  @Input() documentTypes: string[] = [];
  // Optional Inputs
  @Input() filesUploadedlimit = 4;
  @Input() maxFileSize = 20 * 1024 * 1024; // 20Mb
  @Input() maxFileNameSize = 130;
  @Input() multiple = false;
  @Input() filesAcceptableExtensions: string[] = allowedUploadExtensions;
  // Emitted events
  @Output() uploadedFilesChanges = new EventEmitter<Partial<UploadFile>[]>(); //Note: Only Partial UploadFile is being prepared in this component.
  @Output() errorDetected = new EventEmitter<UploadError[]>();

  uploadedFiles: Partial<UploadFile>[] = [];
  isDisabled = false;
  limitReached = false;
  limitReachedMessageText = `You have reached your limit of ${this.filesUploadedlimit} files.`;

  form = new FormGroup({
    description: new FormControl('', Validators.maxLength(40)),
    docTypes: new FormControl([], Validators.required),
  });

  errors: UploadError[] = [];
  filesSizeSum = 0;

  private readonly sanitizer = inject(DomSanitizer);
  private readonly ref: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly notificationService: NotificationService = inject(NotificationService);

  // isExternalUser currently not doing anything, will be needed when replacing existing document-upload in documents-widget to display date picker dialog
  // private readonly shellService: ShellService = inject(ShellService)
  // private isExternalUser = (this.shellService.authenticationService as any).isExternalUser;

  onFileSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    const files: File[] = Array.from<File>(target.files as FileList);
    const validatedFiles = files.reduce((acc: File[], file: File) => {
      if (this.validate(file)) {
        if (this.isImage(file)) {
          // image preview
          (file as any).objectURL = this.sanitizer.bypassSecurityTrustUrl(
            window.URL.createObjectURL(file)
          );
        }
        acc.push(file);
      }
      return acc;
    }, []);
    if (validatedFiles.length !== files.length) {
      this.onError();
      return;
    }
    this.addFile(validatedFiles);
  }

  onClick() {
    this.inputFile.nativeElement.click();
  }

  private validate(file: File): boolean {
    // File type check
    if (this.filesAcceptableExtensions && !this.isFileTypeValid(file)) {
      this.errors.push({ type: 'accept', name: file.name });
    }

    // File size check
    if (this.maxFileSize && file.size > this.maxFileSize) {
      this.errors.push({ type: 'fileSize', name: file.name });
    }

    // File name size check
    if (this.maxFileNameSize && file.name.length > this.maxFileNameSize) {
      this.errors.push({ type: 'fileName', name: file.name });
    }

    // File name validation
    if (!isFileNameValid(file.name).valid) {
      this.errors.push({ type: 'fileNameInvalid', name: file.name });
    }

    if (this.errors.length > 0) {
      return false;
    }
    return true;
  }

  private isFileTypeValid(file: File): boolean {
    for (const type of this.filesAcceptableExtensions) {
      const acceptable = this.isWildcard(type)
        ? this.getTypeClass(file.type) === this.getTypeClass(type)
        : file.type === type ||
          this.getFileExtension(file).toLowerCase() === type.toLowerCase();

      if (acceptable) {
        return true;
      }
    }
    return false;
  }

  private getTypeClass(fileType: string): string {
    return fileType.substring(0, fileType.indexOf('/'));
  }

  private isWildcard(fileType: string): boolean {
    return fileType.indexOf('*') !== -1;
  }

  private getFileExtension(file: File): string {
    return '.' + file.name.split('.').pop();
  }

  private isImage(file: File): boolean {
    return /^image\//.test(file.type);
  }

  private sumAllFileSize() {
    this.filesSizeSum = this.uploadedFiles.reduce(
      (sum, { fileMetaData }) => sum + fileMetaData.size,
      0
    );
  }

  // if nothing passed will used the component errors list. depends if we want to validated only fileSize or everything in one go.
  private onError(errors: UploadError[] = this.errors) {
    const fileTypeErrors = errors.some((e) => e.type === 'accept');
    const fileSizeErrors = errors.some((e) => e.type === 'fileSize');
    const fileNameSizeErrors = errors.some((e) => e.type === 'fileName');
    const fileNameInvalidErrors = errors.some((e) => e.type === 'fileNameInvalid');
    fileTypeErrors
      ? this.notificationService.showError(
          `Please upload only ${this.filesAcceptableExtensions.join(
            ' '
          )} files.`,
          'File format is not valid'
        )
      : null;
    fileSizeErrors
      ? this.notificationService.showError(
          `Maximum upload size is ${this.maxFileSizeToMB(this.maxFileSize)}.`,
          'File size is invalid'
        )
      : null;
    fileNameSizeErrors
      ? this.notificationService.showError(
          `The file name must not exceed ${this.maxFileNameSize} characters. Rename the file and try again.`,
          'File name is too long'
        )
      : null;
      fileNameInvalidErrors
      ? this.notificationService.showError(
          'The file name contains invalid characters. Please rename the file and try again.',
          'Invalid File Name',
        )
      : null;

    // If Parent Component needs to handle error in additional way
    this.errorDetected.emit(errors);
    this.errors = [];
    this.cleanFileInputEl();
  }

  private resetValidationErrors() {
    this.errors = [];
  }

  // make sure no more than files selected
  private checkLimit() {
    this.limitReached = this.uploadedFiles.length >= this.filesUploadedlimit;
  }

  removeItem(ri: number) {
    this.uploadedFiles.splice(ri, 1);
    this.checkLimit();
    this.uploadedFilesChanges.emit(this.uploadedFiles);
  }

  addFile(files: File[]) {
    if (this.uploadedFiles.length < this.filesUploadedlimit) {
      this.addSelected(files);
      this.ref.markForCheck();
      this.cleanFileInputEl();
    }
  }

  private cleanFileInputEl() {
    this.inputFile.nativeElement.value = null;
  }

  // add selected to temp list
  async addSelected(files: FileList | File[], dateTimeFaxReceived?: string) {
    const file: File = files[0];
    this.sumAllFileSize();

    if (this.filesSizeSum + file.size > this.maxFileSize) {
      this.errors.push({ type: 'fileSize', name: file.name });
      this.onError();
    } else {
      this.resetValidationErrors();
      try {
        const fileBase64: string = (await this.readFileAsync(file)) as string;
        const fileContent = fileBase64.split(',')[1];
        this.uploadedFiles.push({
          fileMetaData: {
            size: file.size,
            type: file.type,
          },
          description: this.form.value.description,
          documentSource: 'Upload',
          fileContent,
          documentTypes: this.form.value.docTypes,
          dateTimeFaxReceived: dateTimeFaxReceived || new Date().toUTCString(),
          documentFileName: this.addFilenameTimestamp(file.name),
          isAutomaticOCRDocument: false,
          // IMPORTANT! Leaving those commented below for visibility, Parent component would be responsible to populate those fields if needed.
          // authorizationId: this.authorizationByProductId,
          // authorizationNumber: this.authorizationNumber,
          // tenantId: this.shellService.getTenantId(),
        });

        this.checkLimit();
        this.form.reset();
        this.ref.markForCheck();
        this.uploadedFilesChanges.emit(this.uploadedFiles);
      } catch (error) {
        this.notificationService.showError('Error while uploading file.');
      }
    }

    this.ref.markForCheck();
  }

  private readFileAsync(file: File): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private maxFileSizeToMB(fileSize: number): string {
    return `${(fileSize / 1024 / 1024).toFixed(0).toString()}MB`;
  }

  private addFilenameTimestamp(fileName: string): string {
    const ext = fileName.split('.').pop();
    const name = fileName.split('.').slice(0, -1).join('.');

    const date = new Date();
    const addLeadingZero = (num: number): string => {
      return String(num).padStart(2, '0');
    };
    // Gives timestamp in YYYYMMddHHmmss format (Could switch to pipe or date transform instead)
    const timestamp =
      `${date.getFullYear()}${addLeadingZero(
        date.getMonth() + 1
      )}${addLeadingZero(date.getDate())}` +
      `${addLeadingZero(date.getHours())}${addLeadingZero(
        date.getMinutes()
      )}${addLeadingZero(date.getSeconds())}`;

    return `${name}_${timestamp}.${ext}`;
  }
}
